import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  input,
  OnInit
} from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';

import { FLOORPLAN_GROUPS_STORE } from '@app/floorplans/stores/floorplan-groups.store';
import { FLOORPLANS_STORE } from '@app/floorplans/stores/floorplans.store';
import { ProjectSetupDependencyConfig } from '@app/shared/models/project-setup-dependency-config';
import { PermissionsService } from '@core/services/permissions.service';
import { ACCOUNT_STORE } from '@core/stores/account.store';
import { APP_STORE } from '@core/stores/app.store';
import { PermissionsType } from '@shared/models/permissions-type';
import { RouterService } from '@shared/services/router.service';
import { INSPECTION_ITEM_STATUS_SVG_ICONS_NAMESPACE } from '@shared/svg-icons/consts';
import { MatIconService } from '@shared/svg-icons/services/mat-icon.service';
import { waitForSignalLoaded } from '@shared/utils/wait-for-signal-loaded';
import { SETTINGS_STORE } from '@settings/stores/settings.store';
import { FilesService } from '@file-upload/services/files.service';

@Component({
  selector: 'app-project-setup',
  templateUrl: './project-setup.component.html',
  styleUrls: ['./project-setup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MatProgressSpinner]
})
export class ProjectSetupComponent implements OnInit {
  readonly clientId = input(null);
  readonly projectId = input(null);
  readonly redirectTo = input(null);

  readonly #injector = inject(Injector);
  readonly #router = inject(Router);
  readonly #appStore = inject(APP_STORE);
  readonly #settingsStore = inject(SETTINGS_STORE);
  readonly #accountStore = inject(ACCOUNT_STORE);
  readonly #floorplanGroupsStore = inject(FLOORPLAN_GROUPS_STORE);
  readonly #floorplansStore = inject(FLOORPLANS_STORE);
  readonly #routerService = inject(RouterService);
  readonly #filesService = inject(FilesService);
  readonly #permissionsService = inject(PermissionsService);
  readonly #matIconService = inject(MatIconService);

  readonly errorOccurred = computed(() => {
    const errors = [
      this.#accountStore.permissions.error(),
      this.#settingsStore.attributes().error,
      this.#settingsStore.inspectionItemStatuses().error,
      this.#settingsStore.inspectionReportCustomSections().error,
      this.#settingsStore.accounts().error,
      this.#floorplansStore.error(),
      this.#floorplanGroupsStore.error(),
      this.#accountStore.accountState.error()
    ];
    return errors.some(error => !!error);
  });

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  async ngOnInit(): Promise<void> {
    const projectId = this.projectId();

    if (!projectId) {
      void this.#router.navigate(['/error'], {
        queryParams: { errorType: 'no-project' }
      });
      this.#appStore.setupProjectSuccess(projectId);
      return;
    }

    this.#accountStore.loadAccountPermissions();

    await waitForSignalLoaded(
      this.#accountStore.permissions.loaded,
      this.#injector
    );

    if (!this.hasAccess) {
      void this.#router.navigate(['/error'], {
        queryParams: { errorType: 'no-rights' }
      });
      this.#appStore.setupProjectSuccess(projectId);
      return;
    }

    this.#filesService.cleanupWorkspace();

    await this.loadDependencies();

    if (this.errorOccurred()) {
      void this.#router.navigate(['/error'], {
        queryParams: { errorType: 'project-setup-failure' }
      });
      this.#appStore.setupProjectFailure();
      return;
    }

    this.addInspectionItemStatusesSvgIcons(
      INSPECTION_ITEM_STATUS_SVG_ICONS_NAMESPACE
    );

    const redirectTo = this.redirectTo();
    const routerUrl = redirectTo
      ? `${this.clientId()}/${projectId}/${redirectTo}`
      : '';
    this.#routerService.navigateWithReplace(routerUrl);
    this.#appStore.setupProjectSuccess(projectId);
  }

  get hasAccess(): boolean {
    return (
      this.#permissionsService.hasPermissions([
        PermissionsType.SettingsViewForInspections,
        PermissionsType.SettingsViewForLop,
        PermissionsType.SettingsViewForTemplates
      ]) ||
      this.#permissionsService.hasPermissions([
        PermissionsType.AttributeView,
        PermissionsType.InspectionItemStatusView,
        PermissionsType.AccountView,
        PermissionsType.RoleView,
        PermissionsType.InspectionReportCustomSectionView,
        PermissionsType.EmailNotificationView
      ]) ||
      this.#permissionsService.hasPermissions([
        PermissionsType.FloorplanView,
        PermissionsType.FloorplanViewForInspections
      ])
    );
  }

  get dependenciesConfigs(): ProjectSetupDependencyConfig[] {
    return [
      {
        permissions: [
          PermissionsType.AttributeView,
          PermissionsType.SettingsViewForInspections,
          PermissionsType.SettingsViewForLop,
          PermissionsType.SettingsViewForTemplates
        ],
        loadFunction: () => this.#settingsStore.loadAttributes(),
        loaded: this.#settingsStore.attributes.loaded
      },
      {
        permissions: [
          PermissionsType.InspectionItemStatusView,
          PermissionsType.SettingsViewForInspections
        ],
        loadFunction: () => this.#settingsStore.loadInspectionItemStatuses(),
        loaded: this.#settingsStore.inspectionItemStatuses.loaded
      },
      {
        permissions: [
          PermissionsType.AccountView,
          PermissionsType.SettingsViewForInspections,
          PermissionsType.SettingsViewForLop,
          PermissionsType.SettingsViewForTemplates
        ],
        loadFunction: () => this.#settingsStore.loadAccounts(),
        loaded: this.#settingsStore.accounts.loaded
      },
      {
        permissions: [
          PermissionsType.RoleView,
          PermissionsType.SettingsViewForInspections,
          PermissionsType.SettingsViewForLop,
          PermissionsType.SettingsViewForTemplates
        ],
        loadFunction: () => this.#settingsStore.loadRoles(),
        loaded: this.#settingsStore.roles.loaded
      },
      {
        permissions: [PermissionsType.InspectionReportCustomSectionView],
        loadFunction: () =>
          this.#settingsStore.loadInspectionReportCustomSections(),
        loaded: this.#settingsStore.inspectionReportCustomSections.loaded
      },
      {
        permissions: [
          PermissionsType.FloorplanView,
          PermissionsType.FloorplanViewForInspections,
          PermissionsType.FloorplanViewForLop,
          PermissionsType.FloorplanViewForTemplates
        ],
        loadFunction: (): void => {
          this.#floorplansStore.loadFloorplans();
          this.#floorplanGroupsStore.loadFloorplanGroups();
        },
        loaded: computed(
          () =>
            this.#floorplansStore.loaded() &&
            this.#floorplanGroupsStore.loaded()
        )
      },
      {
        permissions: [PermissionsType.EmailNotificationView],
        loadFunction: () => this.#settingsStore.loadEmailNotifications(),
        loaded: this.#settingsStore.emailNotifications.loaded
      }
    ];
  }

  async loadDependencies(): Promise<void> {
    this.#accountStore.loadAccountState();

    const loaded = [this.#accountStore.accountState.loaded];

    for (const config of this.dependenciesConfigs) {
      if (this.#permissionsService.hasPermissions(config.permissions)) {
        config.loadFunction();

        loaded.push(config.loaded);
      }
    }

    await Promise.all(
      loaded.map(loadedSignal =>
        waitForSignalLoaded(loadedSignal, this.#injector)
      )
    );
  }

  addInspectionItemStatusesSvgIcons(namespace: string): void {
    const iconUrls = this.#settingsStore
      .inspectionItemStatuses()
      .entities.map(x => x.iconUrl);

    this.#matIconService.addSvgIconsInNamespaceByUrl(iconUrls, namespace);
  }
}
